/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import * as _ from 'lodash';
import * as moment from 'moment';
import {APP_SHARED} from '../../common/app-shared';

@Component({
  standalone: true,
  imports: [...APP_SHARED],
  selector: 'adn-workspace-velocity-slim',
  template: `
    @if (showLabel) {
      <div style="margin-bottom: 3px;" class="small">
        <div class="label" [ngClass]="labelClass">{{ labelText }}</div>
      </div>
    }
  `
})
export class WorkspaceVelocitySlimComponent implements OnChanges, OnInit {
  @Input() velocity: number;
  @Input() endDate: any;

  showLabel = false;
  labelClass = 'label-default';
  labelText = '';

  constructor(private readonly translate: TranslateService) {
  }

  ngOnInit(): void {
    this.updateLabel();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.velocity || changes.endDate) {
      this.updateLabel();
    }
  }

  private updateLabel(): void {
    if (!_.isFinite(this.velocity)) {
      this.showLabel = true;
      this.labelClass = 'label-default';
      this.labelText = this.translateLabel('lineItem.delivery.NOT_APPLICABLE');
      return;
    }
    if (!this.endDate || !_.isDate(moment(this.endDate).toDate())) {
      this.showLabel = true;
      this.labelClass = 'label-default';
      this.labelText = this.translateLabel('lineItem.delivery.NO_END_DATE');
      return;
    }

    const rawPercentage = (Math.pow(1.25, this.velocity) - 1) * 100;
    const roundedPercentage = Math.sign(rawPercentage) * Math.ceil(Math.abs(rawPercentage) / 5) * 5;
    let outputPercentage = roundedPercentage.toString();
    this.showLabel = true;

    if (roundedPercentage > 50) {
      this.labelClass = 'label-danger';
      this.labelText = this.translateLabel('lineItem.delivery.OVER', outputPercentage);
    } else if (roundedPercentage > 20) {
      this.labelClass = 'label-warning';
      this.labelText = this.translateLabel('lineItem.delivery.OVER', outputPercentage);
    } else if (roundedPercentage < -20) {
      this.labelClass = 'label-danger';
      this.labelText = this.translateLabel('lineItem.delivery.UNDER', outputPercentage);
    } else if (roundedPercentage === 0) {
      this.labelClass = 'label-primary';
      this.labelText = this.translateLabel('lineItem.delivery.HEALTHY_ON_TARGET', outputPercentage);
    } else {
      this.labelClass = 'label-primary';
      outputPercentage = roundedPercentage > 0 ? ('+' + roundedPercentage) : roundedPercentage.toString();
      this.labelText = this.translateLabel('lineItem.delivery.HEALTHY', outputPercentage);
    }
  }

  private translateLabel(key: string, percentage = ''): string {
    return this.translate.instant(key, {percentage});
  }
}
